import * as React from 'react';
import { connect } from 'react-redux';

import { faArrowUpRightFromSquare, faLink } from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ExcelUploadAccountingPackageNameId, UniversalCollectorAccountingPackageNameId } from '../../Models/Api/AccountingPackage';
import { ConnectionDescriptor, PortalTextAccountingSystems, PortalTextCommon, ShareableLink } from '../../Models/Api/strongbox.financialportal';
import { ApplicationState } from '../../Store';
import { GetEnabledAccountingPackagePresentations, IsAccountingPackageConnectorOnly } from '../../Store/AppSettings';
import {
    CustomerFinancialsTarget,
    GetImportCustomerFinancialsTarget,
} from '../../Store/ImportFinancials';
import {
    FinancialsImportFlowView,
    GetAccountingSystemConnectionError,
    GetConnectEndpoint,
    GetCurrentSubmissionAccountingSystem,
    GetExistingConnectionForCurrentSubmission,
    GetFinancialsImportFlowView,
    GetShareLinkInfo,
    IConnectionError,
    actionCreators as NewDataCollectionParametersActions,
} from '../../Store/NewDataCollectionParameters';
import { GetPortalText } from '../../Store/PortalAssets';
import { GetBrandConfig } from '../../Store/Tenant';
import { actionCreators as UIStateActions } from '../../Store/UIState';
import { CopyToClipboard } from '../../Utils/ClipboardUtils';
import { ConnectWindow } from '../../Utils/ConnectWindow';
import { LogException } from '../../Utils/Logging';
import { StringFormat } from '../../Utils/StringUtils';
import { DeleteWorkspaceConnection } from '../../Services/WorkspaceConnectionsService';
import { AccountingSystemSelectorModal } from '../AccountingSystemSelectorModal/AccountingSystemSelectorModal';
import { ConnectionConfirmationModal } from '../ConnectionConfirmationModal/ConnectionConfirmationModal';
import { Overlay } from '../Overlay/Overlay';
import { Button as DSButton, ModalHeader as DSModalHeader } from '../design-system/components';
import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator';
import '../ImportCustomerFinancials/NewConnectionModal.scss';
import { IAccountingPackagePresentation } from '../SelectAccountingSystem/AccountingPkgPresentation';
import { LenderLinkModal as ShareLinkModal } from '../ShareLinkModal/ShareLinkModal';
import './DataCollectionImportFlow.scss';

type InjectedReduxState = {
    connectionOptionsTarget: CustomerFinancialsTarget | undefined;
    flowView: FinancialsImportFlowView;
    shareLinkInfo: ShareableLink | null;
    currentSubmissionAccountingSystem: string | null;
    currentSubmissionIsConnectorOnly: boolean;
    connectionError: IConnectionError | undefined;
    brandAccentColor?: string;
    commonText?: PortalTextCommon;
    accountingPackagePresentations: IAccountingPackagePresentation[];
    accountingSystemText: PortalTextAccountingSystems;
    hasExistingConnection: boolean;
    existingConnection: ConnectionDescriptor | null | undefined;
    connectEndpoint: string | null;
};

type InjectedActionCreators = typeof NewDataCollectionParametersActions
    & Pick<typeof UIStateActions, 'SetPortalWorking' | 'SetPortalIdle'>;

type DataCollectionImportFlowProps = {
    // Invoked once the import has been kicked off (both the connect and reuse-connection paths). The
    // lender flow leaves this undefined and relies on CompleteCurrentSubmission tearing the flow down;
    // the borrower flow uses it to navigate on to the progress step.
    onImportStarted?: () => void;
};

// Id for the standardized portal busy state (SetPortalWorking / SetPortalIdle) used in place of a bespoke
// "Working…" modal for the generic 'working' flow view. The counter tolerates concurrent sources, so a
// stable id per source is all that is required.
const WORKING_SOURCE = 'DataCollectionImportFlow';

type Props = DataCollectionImportFlowProps & InjectedActionCreators & InjectedReduxState;

/**
 * A modal that shows a spinner with a status message while the flow is processing. Used both for the
 * generic network-wait ("Working…") view and the popup-wait ("Waiting on connection…") view. A Cancel
 * button is rendered only when an onCancel handler is supplied.
 */
const ProcessingModal: React.FC<{ message: string; onCancel?: () => void; commonText?: PortalTextCommon }> = ({ message, onCancel, commonText }): React.ReactElement => (
    <Overlay>
        <div className={'cr-modal page-modal'} style={{ width: 600 }}>
            <DSModalHeader title={message} hasCloseButton={false} />
            <div className={'cr-modal-body page-modal-body'}>
                <div className={'page-centered-content'}>
                    <LoadingIndicator active={true} showSpinner={true} />
                </div>
            </div>
            {!!onCancel && (
                <div className={'cr-modal-footer'}>
                    <DSButton type={'floating'} onClick={onCancel}>{commonText?.cancel || 'Cancel'}</DSButton>
                </div>
            )}
        </div>
    </Overlay>
);

/**
 * Shown while the universal collector ("upload files yourself") connect popup is open, in place of the
 * generic "Waiting on connection…" spinner. The universal collector is a one-way file upload the user
 * completes in the popup, so instead of a spinner we tell them to finish over there and give a manual
 * fallback link (Copy Link) in case the popup was blocked. This mirrors V1's
 * RenderConnectWithUniversalConnector. Close aborts the flow (closes the popup, tears the flow down).
 */
const UniversalConnectorModal: React.FC<{ connectEndpoint: string; onClose: () => void; commonText?: PortalTextCommon }> = ({ connectEndpoint, onClose, commonText }): React.ReactElement => (
    <Overlay>
        <div className={'cr-modal page-modal'} style={{ width: 600 }}>
            <DSModalHeader title={commonText?.uploadFiles || 'Upload files'} onClose={onClose} />
            <div className={'cr-modal-body page-modal-body'}>
                <div className={'page-centered-content page-centered-content--gap-medium'}>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={'page-large-icon'} />
                    <div className={'page-stack page-stack--tight'}>
                        <span className={'page-section-label'} style={{ color: 'var(--text-color-primary-default)' }}>
                            {commonText?.continueInNewWindow || 'Continue in new window'}
                        </span>
                        <span className={'page-body-text'} style={{ maxWidth: 360 }}>{commonText?.finishUCoverThere || ''}</span>
                    </div>
                    <DSButton
                        type={'secondary'}
                        leadingIcon={<FontAwesomeIcon icon={faLink} />}
                        onClick={() => CopyToClipboard(connectEndpoint)}
                    >
                        {commonText?.copyLink || 'Copy link'}
                    </DSButton>
                </div>
            </div>
            <div className={'cr-modal-footer'}>
                <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
                    <DSButton type={'primary'} className={'page-btn--min-width'} onClick={onClose}>
                        {commonText?.closeButton || 'Close'}
                    </DSButton>
                </div>
            </div>
        </div>
    </Overlay>
);

/**
 * The store-driven flow-view machine shared by the lender import (FinancialsImportV2) and the borrower
 * portal (PortalSubmissionV2). It renders exactly one view for the current NewDataCollectionParameters
 * state (see GetFinancialsImportFlowView) and owns the connect handlers, so both flows drive the same
 * confirm/connect/working/error UI. It renders nothing when no flow is in progress ('none'); the caller
 * is responsible for the idle content (the import wizard, or the borrower's package selection UI) and
 * for kicking the flow off via SetCurrentSubmissionRequest / SetSelectedAccountingPackage.
 */
const DataCollectionImportFlowComponent: React.FC<Props> = (props): React.ReactElement | null => {
    const {
        connectionOptionsTarget,
        ClearCurrentSubmissionRequest,
        CompleteCurrentSubmission,
        SetSelectedAccountingPackage,
        flowView,
        shareLinkInfo,
        currentSubmissionAccountingSystem,
        currentSubmissionIsConnectorOnly,
        CreateWorkspaceSubmissionAndStartImport,
        CreateWorkspaceSubmissionAndReuseConnection,
        accountingPackagePresentations,
        ReportPopupBlocked,
        connectionError,
        brandAccentColor,
        commonText,
        accountingSystemText,
        hasExistingConnection,
        existingConnection,
        connectEndpoint,
        onImportStarted,
        SetPortalWorking,
        SetPortalIdle,
    } = props;

    const currentSubmissionIsUniversal = currentSubmissionAccountingSystem?.toLowerCase() === UniversalCollectorAccountingPackageNameId;

    // The generic 'working' view no longer renders a bespoke modal; it drives the standardized portal busy
    // state instead (LenderPortal's BusyWindowWrapper / BorrowerPortalV2's loading indicator). Push on entry
    // to 'working' and pop on exit (or unmount) so the counter stays balanced across concurrent sources.
    React.useEffect(() => {
        if (flowView !== 'working') {
            return;
        }
        SetPortalWorking(WORKING_SOURCE);
        return () => {
            SetPortalIdle(WORKING_SOURCE);
        };
    }, [flowView, SetPortalWorking, SetPortalIdle]);

    // Tracks the in-flight connect popup and a cancel signal so the "Waiting on connection…" modal's
    // Cancel button can stop the poll loop (via the isCancelled predicate), close the popup, and exit.
    const cancelConnectionRef = React.useRef(false);
    const connectWindowRef = React.useRef<ConnectWindow | null>(null);

    // True from the moment the user clicks Connect in the confirmation modal until the attempt resolves.
    // While set, we keep the confirmation modal mounted showing a spinner (see the render short-circuit
    // below) instead of swapping in the generic working / waiting-on-connection modals.
    const [connecting, setConnecting] = React.useState<boolean>(false);

    // True while disconnecting an existing connection (the "Disconnect" action). Shares the connecting spinner
    // render so the modal stays up with a spinner until the connection is removed and the lookup re-runs.
    const [disconnecting, setDisconnecting] = React.useState<boolean>(false);

    const handleOnCancel = () => {
        ClearCurrentSubmissionRequest();
        return true;
    }

    const handleOnBack = () => {
        SetSelectedAccountingPackage(null);
    }

    const handleCancelConnection = () => {
        // Cancel an in-flight connection: signal the poll loop to stop (isCancelled), close the popup,
        // and exit the flow.
        cancelConnectionRef.current = true;
        connectWindowRef.current?.close();
        ClearCurrentSubmissionRequest();
    }

    const handleOnConnect = async () => {
        // The popup MUST be opened synchronously here, inside the click handler, so the browser
        // grants transient activation for window.open. The async setup then navigates this already-
        // open window to the real connect URL (see StartWorkspaceSubmissionImport).
        const connectWindow = new ConnectWindow();

        const accountingPackage = accountingPackagePresentations.find(x => (x.featureName as string).toLowerCase() === currentSubmissionAccountingSystem?.toLowerCase());
        // The excel / external-file upload package isn't in the presentations list; resolve its descriptor from
        // portal text (excelFileUploadDescriptor) so the popup heading reads correctly.
        const accountingDescriptor = currentSubmissionAccountingSystem?.toLowerCase() === ExcelUploadAccountingPackageNameId.toLowerCase()
            ? commonText?.excelFileUploadDescriptor
            : accountingPackage?.descriptor;
        const heading = StringFormat(accountingSystemText?.templateConnectingToAccountingSystem || 'Connect to your accounting system', accountingDescriptor);
        const opened = connectWindow.open({
            heading: heading || 'Connecting to your accounting system…',
            subText: 'Please keep this window open while we finish connecting.',
            accentColor: brandAccentColor,
        });

        if (!opened) {
            ReportPopupBlocked(
                'Popup blocked',
                'Your browser blocked the connection window. Please allow popups for this site and try again.'
            );
            return;
        }

        // Reset the cancel signal for this attempt and remember the popup so the Cancel button can close
        // it. isCancelled reads the ref live, so flipping it later stops the poll loop.
        cancelConnectionRef.current = false;
        connectWindowRef.current = connectWindow;

        // Keep the confirmation modal on screen with a spinner for the whole attempt. On success the flow
        // tears itself down (CompleteCurrentSubmission → flowView 'none'); on any failure it reverts to the
        // confirmation modal ('aborted' → 'confirmConnect'), so clearing this flag simply restores the buttons.
        setConnecting(true);
        const succeeded = await CreateWorkspaceSubmissionAndStartImport(connectWindow, () => cancelConnectionRef.current);
        setConnecting(false);

        // Only advance past the modal when the connect + import actually started; a failed attempt must
        // stay on (well, revert to) the confirmation modal so the user can retry.
        if (succeeded) {
            onImportStarted?.();
        }
    }

    const handleOnReuse = async () => {
        // Reuse the workspace's existing connection: no OAuth popup is needed, so we just create the
        // submission and start the import directly against that connection.
        await CreateWorkspaceSubmissionAndReuseConnection();
        onImportStarted?.();
    }

    const handleOnDisconnect = async () => {
        // "Disconnect": forget the existing connection, then re-run the package lookup so the confirmation
        // modal reverts to the plain "Connect" state (as if there had never been an existing connection).
        if (!existingConnection || !currentSubmissionAccountingSystem) {
            return;
        }
        setDisconnecting(true);
        try {
            await DeleteWorkspaceConnection(existingConnection.orgId, existingConnection.id);
            // Re-select the same package: the connection is gone now, so the lookup resolves to
            // notFound and hasExistingConnection flips false → the Connect button reappears.
            await SetSelectedAccountingPackage(currentSubmissionAccountingSystem);
        } catch (e) {
            LogException('Failed to disconnect the existing connection', e, { connectionId: existingConnection.id });
        } finally {
            setDisconnecting(false);
        }
    }

    // A connect attempt (or a disconnect) launched from the confirmation modal is in flight: keep THAT modal
    // on screen with a spinner (no buttons) for the whole attempt, rather than letting the intermediate
    // working / waiting-on-connection flow states swap in a different modal. Only set for the OAuth
    // confirm-and-connect / disconnect path; the universal collector uses its own modal and never sets it.
    if (connecting || disconnecting) {
        return (
            <ConnectionConfirmationModal
                connecting={true}
                accountingSystem={currentSubmissionAccountingSystem ?? ''}
                hasExistingConnection={hasExistingConnection}
                onConnect={handleOnConnect}
                onReuseConnection={handleOnReuse}
                onDisconnect={handleOnDisconnect}
                onCancel={handleOnCancel}
                onBack={handleOnBack}
            />
        );
    }

    // Every reachable store state maps to exactly one view here (see GetFinancialsImportFlowView). The
    // never check makes that exhaustiveness a compile-time guarantee.
    switch (flowView) {
        case 'none':
            return null;
        case 'working':
            // No bespoke modal — the effect above drives the standardized portal busy indicator instead.
            return null;
        case 'waitingOnConnection':
            // The universal collector is a file upload the user completes in the popup. Once we have the
            // connect URL, swap the generic spinner for the "complete in the new window / copy link" modal
            // (matches V1). Until the URL is resolved (token/descriptor round-trip) we still show the spinner.
            if ((currentSubmissionIsUniversal || currentSubmissionIsConnectorOnly) && !!connectEndpoint) {
                // The universal collector doesn't auto-complete (no poll) — the upload happens in the popup
                // out-of-band. Close finalizes the already-created submission (CompleteCurrentSubmission)
                // rather than cancelling it.
                return <UniversalConnectorModal connectEndpoint={connectEndpoint} onClose={() => CompleteCurrentSubmission()} commonText={commonText} />;
            }
            return <ProcessingModal message={'Waiting on connection…'} onCancel={() => handleCancelConnection()} commonText={commonText} />;
        case 'selectAccountingSystem':
            return (
                <AccountingSystemSelectorModal
                    workspaceName={connectionOptionsTarget?.workspaceName}
                    linkDetails={shareLinkInfo}
                    onClose={() => { ClearCurrentSubmissionRequest() }}
                />
            );
        case 'shareLink':
            return (
                <ShareLinkModal
                    workspaceName={connectionOptionsTarget?.workspaceName}
                    linkDetails={shareLinkInfo}
                    onComplete={() => { ClearCurrentSubmissionRequest() }}
                />
            );
        case 'confirmConnect':
            // Serves both the first confirmation and the post-abort retry prompt: Connect retries,
            // Cancel aborts the whole flow.
            return (
                <ConnectionConfirmationModal
                    accountingSystem={currentSubmissionAccountingSystem ?? ''}
                    hasExistingConnection={hasExistingConnection}
                    onConnect={handleOnConnect}
                    onReuseConnection={handleOnReuse}
                    onDisconnect={handleOnDisconnect}
                    onCancel={handleOnCancel}
                    onBack={handleOnBack}
                />
            );
        case 'error':
            // A connector error from the OAuth connect attempt surfaces in the SAME "Connect to {system}"
            // modal the user was waiting in (Cancel / Try again), rather than a separate error modal. Other
            // 'error' sources (request/submission creation) carry no connectionError and fall through to the
            // generic modal below. The universal collector has its own modal, so it is excluded here too.
            if (connectionError && currentSubmissionAccountingSystem && !currentSubmissionIsUniversal) {
                return (
                    <ConnectionConfirmationModal
                        accountingSystem={currentSubmissionAccountingSystem}
                        hasExistingConnection={hasExistingConnection}
                        connectionError={connectionError}
                        onConnect={handleOnConnect}
                        onReuseConnection={handleOnReuse}
                        onDisconnect={handleOnDisconnect}
                        onCancel={handleOnCancel}
                        onBack={handleOnBack}
                    />
                );
            }
            return (
                <Overlay>
                    <div className={'cr-modal page-modal'} style={{ width: 600 }}>
                        <DSModalHeader title={connectionError?.msg || commonText?.error || 'Error'} hasCloseButton={false} />
                        <div className={'cr-modal-body page-modal-body'}>
                            <p className={'page-body-text'}>{connectionError?.detailedMsg || connectionError?.msg || 'Something went wrong…'}</p>
                        </div>
                        <div className={'cr-modal-footer'}>
                            <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
                                <DSButton type={'primary'} className={'page-btn--min-width'} onClick={() => handleOnCancel()}>
                                    {commonText?.okay || 'OK'}
                                </DSButton>
                            </div>
                        </div>
                    </div>
                </Overlay>
            );
        default: {
            const _exhaustive: never = flowView;
            return _exhaustive;
        }
    }
};

export const DataCollectionImportFlow = connect<InjectedReduxState, InjectedActionCreators, DataCollectionImportFlowProps, ApplicationState>(
    (appState: ApplicationState) => {
        const brandPalette = GetBrandConfig(appState)?.themeSettings?.palette;

        const result: InjectedReduxState = {
            connectionOptionsTarget: GetImportCustomerFinancialsTarget(appState),
            flowView: GetFinancialsImportFlowView(appState),
            shareLinkInfo: GetShareLinkInfo(appState),
            currentSubmissionAccountingSystem: GetCurrentSubmissionAccountingSystem(appState),
            currentSubmissionIsConnectorOnly: IsAccountingPackageConnectorOnly(appState, GetCurrentSubmissionAccountingSystem(appState)),
            connectionError: GetAccountingSystemConnectionError(appState),
            brandAccentColor: brandPalette?.primaryColor || brandPalette?.controlBackground,
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            accountingSystemText: GetPortalText<PortalTextAccountingSystems>(appState, 'accountingSystems'),
            accountingPackagePresentations: GetEnabledAccountingPackagePresentations(appState),
            hasExistingConnection: !!GetExistingConnectionForCurrentSubmission(appState),
            existingConnection: GetExistingConnectionForCurrentSubmission(appState),
            connectEndpoint: GetConnectEndpoint(appState),
        };

        return result;
    },
    {
        ...NewDataCollectionParametersActions,
        SetPortalWorking: UIStateActions.SetPortalWorking,
        SetPortalIdle: UIStateActions.SetPortalIdle,
    }
)(DataCollectionImportFlowComponent as any);
